import gsap from 'gsap';
import { CONFIG, KEYCODES, ANIMATIONS } from '../config';

import Hammer from '../../tools/hammer';

class BfvBildergalerieSlider {
  constructor(options = {}) {
    this.$root = options.element;
    this.$scrollable = this.$root.querySelector('.js-scrollable');
    this.$slidesArray = [...this.$root.querySelectorAll('.js-slide')];
    this.$currentSlideIndicator = this.$root.querySelector('.js-current-slide-indicator');
    this.$prevButton = this.$root.querySelector('.js-prev-button');
    this.$nextButton = this.$root.querySelector('.js-next-button');
    this.$currentSlideIndicator = this.$root.querySelector('.js-current-slide-indicator');
    this.slideCount = this.$slidesArray.length;
    this.index = 0;
    this.animatedIndex = null;

    this.bounceAnimationValue = 20; // percentage value
  }

  init() {
    this.updateActivePrevNextSlide();
    this.bindEvents();
    this.toggleNextPrevButtonVisibility();
  }

  updateActivePrevNextSlide() {
    this.$activeSlide = this.$slidesArray[this.index];
    this.$prevSlide = this.$slidesArray[this.index - 1];
    this.$nextSlide = this.$slidesArray[this.index + 1];

    this.$currentSlideIndicator.innerText = this.index + 1;
  }

  toggleNextPrevButtonVisibility() {

    if (this.index < this.$slidesArray.length - 1) {
      this.$nextButton.classList.add('js-button-visible');
    } else {
      this.$nextButton.classList.remove('js-button-visible');
    }

    if (this.index > 0) {
      this.$prevButton.classList.add('js-button-visible');
    } else {
      this.$prevButton.classList.remove('js-button-visible');
    }
  }

  bindEvents() {
    // left / right keys for active element
    const handleKeyDown = (event) => {
      const key = event.keyCode || event.which;
      if (key === KEYCODES.LEFT) {
        this.prev();
      } else if (key === KEYCODES.RIGHT) {
        this.next();
      }
    };

    this.$root.addEventListener('mouseenter', () => {
      this.$root.focus();
      document.activeElement.addEventListener('keydown', handleKeyDown, true);
    });
    this.$root.addEventListener('mouseleave', () => {
      this.$root.blur();
      document.activeElement.removeEventListener('keydown', handleKeyDown, true);
    });

    // Peak Animation
    this.$nextButton.addEventListener('mouseenter', () => {
      this.peakInAnimation('NEXT');
    });
    this.$prevButton.addEventListener('mouseenter', () => {
      this.peakInAnimation('PREV');
    });
    this.$nextButton.addEventListener('mouseleave', () => {
      this.peakOutAnimation();
    });
    this.$prevButton.addEventListener('mouseleave', () => {
      this.peakOutAnimation();
    });

    // Click Events
    this.$nextButton.addEventListener('click', (ev) => {
      ev.preventDefault();
      this.next();
    });
    this.$prevButton.addEventListener('click', (ev) => {
      ev.preventDefault();
      this.prev();
    });

    // Mobile -- Swipe and Drag
    let lastPanXValue = 0;
    let startPanXValue = 0;
    let ignorePan = false;
    const hammertime = new Hammer(this.$scrollable);
    hammertime.get('swipe').set({ direction: Hammer.DIRECTION_HORIZONTAL });
    hammertime.get('pan').set({ direction: Hammer.DIRECTION_HORIZONTAL });

    // SWIPE
    hammertime.on('swipeleft', () => {
      ignorePan = true;
      this.next();
    });

    hammertime.on('swiperight', () => {
      ignorePan = true;
      this.prev();
    });

    // PAN
    hammertime.on('panstart', (ev) => {
      ignorePan = false;
      lastPanXValue = ev.center.x;
      startPanXValue = lastPanXValue;
    });

    hammertime.on('panright panleft', (ev) => {
      const currentPanXValue = ev.center.x;
      this.handlePan(lastPanXValue, currentPanXValue, startPanXValue);
      lastPanXValue = currentPanXValue;
    });

    hammertime.on('panend pancancel', (ev) => {

      if (!ignorePan) {
        this.handlePanEnd(ev.center.x, startPanXValue);
      }

      // Reset
      lastPanXValue = 0;
    });
  }

  handlePan(lastPanXValue, currentPanXValue, startPanXValue) {
    const completeXDiff = startPanXValue - currentPanXValue;
    const completeDiffPositiveValue = Math.abs(completeXDiff);
    const width = this.$scrollable.offsetWidth;
    const diff = lastPanXValue - currentPanXValue;

    // width  = 0.4 = 100%
    const nextOpacityValue = (completeDiffPositiveValue / (width / 100)) * (0.4 / 100);

    let $targetSlideOpacity = this.$prevSlide;
    if (completeXDiff >= 0) {
      $targetSlideOpacity = this.$nextSlide;
    }

    if ($targetSlideOpacity) {
      const opacity = 0.6 + nextOpacityValue;
      gsap.set($targetSlideOpacity, {
        opacity: opacity > 1 ? 1 : opacity,
      });
    }

    const opacity = 1.0 - nextOpacityValue;
    gsap.set(this.$activeSlide, {
      opacity: opacity < 0.6 ? 0.6 : opacity,
    });

    gsap.set(this.$scrollable, {
      x: `-=${diff}`,
    });
  }

  handlePanEnd(panendXValue, startPanXValue) {
    const completeXDiff = startPanXValue - panendXValue;

    if (completeXDiff > 100) {
      this.next();
    } else if (completeXDiff <= 0 && completeXDiff < -100) {
      this.prev();
    } else {
      this.animateToIndex();
    }
  }

  prev() {
    if (this.index === 0) {
      this.bounceAnimation('PREV');
      return;
    } if (this.animatedIndex !== null) {
      return;
    }

    this.index -= 1;
    this.animateToIndex();
  }

  next() {
    if (this.index >= (this.slideCount - 1)) {
      this.bounceAnimation('NEXT');
      return;
    } if (this.animatedIndex !== null) {
      return;
    }
    this.index += 1;
    this.animateToIndex();
  }

  animateToIndex(forceAnimation = false) {
    if (this.animatedIndex !== null && !forceAnimation) {
      return;
    }
    this.animatedIndex = this.index;

    this.updateActivePrevNextSlide();
    this.toggleNextPrevButtonVisibility();

    // Filter out undefined neighbours (e.g. no next slide on the last slide),
    // otherwise GSAP crashes on the undefined target and animatedIndex never
    // gets reset in onComplete, which permanently locks navigation.
    const transparentSlides = [this.$nextSlide, this.$prevSlide].filter(Boolean);

    if (this.$nextSlide) {
      BfvBildergalerieSlider.triggerImageLoadForSlide(this.$nextSlide);
    }

    // Opacity animation for prev & next
    gsap.killTweensOf(transparentSlides);
    gsap.to(transparentSlides, ANIMATIONS.default.HALF_DURATION, {
      opacity: '0.6',
      delay: ANIMATIONS.default.HALF_DURATION,
      ease: ANIMATIONS.default.CURVE,
    });

    gsap.killTweensOf(this.$activeSlide);
    gsap.to(this.$activeSlide, ANIMATIONS.default.HALF_DURATION, {
      opacity: '1',
      ease: ANIMATIONS.default.CURVE,
    });

    // Slide Animation
    gsap.killTweensOf(this.$scrollable);
    gsap.to(this.$scrollable, ANIMATIONS.default.DURATION, {
      x: `${this.index * -1 * 100}%`,
      ease: ANIMATIONS.default.CURVE,
      onComplete: () => {
        this.animatedIndex = null;
      },
    });
  }

  static triggerImageLoadForSlide($slide) {
    const $imageElement = $slide.querySelector('.bfv-image--load-init[data-module="BfvImage"]');
    if ($imageElement) {
      const event = document.createEvent('event');
      event.initEvent(CONFIG.events.BFV_IMAGE_LOAD, false, true);
      $imageElement.dispatchEvent(event);
    }
  }

  bounceAnimation(calledFrom) {
    const indexPercentValue = this.index * -1 * 100;
    const toValue = (calledFrom === 'PREV') ? `${indexPercentValue + this.bounceAnimationValue}%` : `${indexPercentValue - this.bounceAnimationValue}%`;

    gsap.to(this.$activeSlide, ANIMATIONS.default.HALF_DURATION, {
      opacity: 1.0,
    });

    gsap.to(this.$scrollable, ANIMATIONS.bounce.DURATION, {
      x: toValue,
    });

    gsap.to(this.$scrollable, ANIMATIONS.bounce.OUT_DURATION, {
      x: `${indexPercentValue}%`,
      delay: ANIMATIONS.bounce.DELAY,
      ease: ANIMATIONS.bounce.CURVE,
    });
  }

  peakInAnimation(mode) {
    const animationXValue = mode === 'NEXT' ? -10 : 10;
    const nextPrevSlides = [this.$nextSlide, this.$prevSlide].filter(Boolean);
    gsap.killTweensOf(nextPrevSlides);
    gsap.set(nextPrevSlides, {
      opacity: 1,
    });

    gsap.killTweensOf(this.$scrollable);
    gsap.to(this.$scrollable, ANIMATIONS.default.DURATION, {
      x: `${(this.index * -1 * 100) + animationXValue}%`,
      ease: ANIMATIONS.default.CURVE,
    });
  }

  peakOutAnimation() {
    this.animateToIndex(true);
  }
}

export default BfvBildergalerieSlider;
